import React from 'react';
import { Phone, Mail, CheckCircle, Edit2, Trash2, UserCheck, ChevronDown, RotateCcw, Briefcase } from 'lucide-react';
import { formatPhoneNumber, formatCurrency, getInitials } from '../../utils/helpers';
import MapLink from '../MapLink';

/**
 * Individual lead card display for the list view
 * Now with clear distinction between NEW and RETURNING customers
 */
const LeadCard = ({
  lead,
  isOwner,
  onCardClick,
  onUpdateStatus,
  onEdit,
  onConvert,
  onDelete,
  hasCompleteAddress,
  getDaysAgo,
  getFollowUpInfo,
  getStatusColor,
}) => {
  // Determine if this is a returning customer
  const isReturningCustomer = !!lead.matchedCustomerId;
  const isLost = lead.status === 'Lost';
  const followUp = getFollowUpInfo ? getFollowUpInfo(lead) : null;

  return (
    <div
      onClick={() => onCardClick(lead)}
      className="card p-0 overflow-hidden hover:shadow-md transition-all cursor-pointer"
    >
      <div className="p-4">
        {/* Top Row: Avatar, Name, Status */}
        <div className="flex items-start gap-3">
          {/* Avatar with returning customer indicator */}
          <div className="relative">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
              isReturningCustomer 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-primary-100 text-primary-700'
            }`}>
              {getInitials(lead.name)}
            </div>
            {/* Returning customer badge on avatar */}
            {isReturningCustomer && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <RotateCcw size={10} className="text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{lead.name}</h3>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-500">{getDaysAgo(lead.createdAt)}</span>
                  {lead.source && lead.source !== 'Manual Entry' && (
                    <span className="text-xs text-green-600 font-medium">• {lead.source}</span>
                  )}
                  {/* RETURNING CUSTOMER BADGE - Prominent! */}
                  {isReturningCustomer && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                      <RotateCcw size={10} />
                      Returning
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(lead.status)}`}>
                  {lead.status}
                </span>
                {isLost && lead.lostReason && (
                  <span className="text-xs text-gray-500 text-right">{lead.lostReason}</span>
                )}
                {lead.isVerified && (
                  <span className="text-xs text-green-600 flex items-center gap-0.5">
                    <CheckCircle size={10} /> Verified
                  </span>
                )}
                {!hasCompleteAddress(lead) && lead.status !== 'Converted' && (
                  <span className="text-xs text-orange-600 flex items-center gap-0.5">
                    ⚠️ Incomplete
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Address - clickable to map */}
        {(lead.address || lead.city) && (
          <div className="mt-2">
            <MapLink 
              address={lead.address}
              city={lead.city}
              state={lead.state}
              zipCode={lead.zipCode}
              iconSize={12}
              className="text-sm text-gray-500"
              truncate
            />
          </div>
        )}
        {/* Contact & Service - Compact row */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
          {lead.phone && (
            <span className="flex items-center gap-1">
              <Phone size={12} />
              <span className="sm:hidden">{formatPhoneNumber(lead.phone)}</span>
              <a 
                href={`tel:${lead.phone}`} 
                className="hidden sm:inline hover:text-primary-600"
                onClick={(e) => e.stopPropagation()}
              >
                {formatPhoneNumber(lead.phone)}
              </a>
            </span>
          )}
          {lead.email && (
            <span className="flex items-center gap-1">
              <Mail size={12} />
              <span className="truncate max-w-[150px] sm:hidden">{lead.email}</span>
              <a 
                href={`mailto:${lead.email}`} 
                className="hidden sm:inline truncate max-w-[200px] hover:text-primary-600"
                onClick={(e) => e.stopPropagation()}
              >
                {lead.email}
              </a>
            </span>
          )}
        </div>

        {/* Service & Amount row */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {lead.serviceInterest && (
            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
              {lead.serviceInterest}
            </span>
          )}
          {/* Follow-up reminder */}
          {followUp && (
            <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
              followUp.isDue ? 'bg-amber-100 text-amber-800 font-semibold' : 'bg-gray-100 text-gray-600'
            }`}>
              ⏰ {followUp.label}
            </span>
          )}
          {/* Instant Quote indicator */}
          {lead.hasInstantQuote && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
              ✨ Instant Quote
            </span>
          )}
          {/* OWNER ONLY: Custom Quote Needed indicator (no instant quote and no estimated amount) */}
          {isOwner && !lead.hasInstantQuote && !lead.estimatedAmount && lead.status !== 'Converted' && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
              📝 Quote Needed
            </span>
          )}
          {/* OWNER ONLY: Estimated Amount */}
          {isOwner && lead.estimatedAmount && (
            lead.hasInstantQuote ? (
              <span
                className="text-xs font-semibold text-green-600 cursor-help"
                title={`Midpoint of the quoted range (${formatCurrency(lead.estimatedAmount * 0.85)} to ${formatCurrency(lead.estimatedAmount * 1.15)}). Used as the job quote amount if you convert. Not a price commitment.`}
              >
                Mid {formatCurrency(lead.estimatedAmount)}
              </span>
            ) : (
              <span className="text-xs font-semibold text-green-600">
                {formatCurrency(lead.estimatedAmount)}
              </span>
            )
          )}
          {/* OWNER ONLY: Discount badge */}
          {isOwner && lead.isDiscountEligible && (
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
              🎖️ Discount
            </span>
          )}
        </div>

        {/* Message preview */}
        {lead.message && (
          <p className="mt-2 text-sm text-gray-500 line-clamp-1 italic">
            "{lead.message}"
          </p>
        )}
      </div>

      {/* Quick Actions - Visible on all screen sizes */}
      <div className="border-t border-gray-100 p-2 bg-gray-50">
        {/* Status dropdown - Desktop only, NOT for returning customers */}
        <div className="hidden sm:flex items-center gap-2 mb-2">
          {/* Converted leads are read-only */}
          {lead.status === 'Converted' ? (
            <span className="text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-lg font-medium">
              ✓ Converted to Customer
            </span>
          ) : isReturningCustomer ? (
            /* Returning customer - no status dropdown needed, just show info */
            <span className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg font-medium">
              {isLost ? 'Marked as Lost' : 'Ready to create job'}
            </span>
          ) : (
            <>
              <div className="relative">
                <select
                  value={lead.status}
                  onChange={(e) => {
                    e.stopPropagation();
                    onUpdateStatus(lead.id, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Quoted">Quoted</option>
                  <option value="Lost">Lost</option>
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              {!hasCompleteAddress(lead) && (
                <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                  ⚠️ Missing address
                </span>
              )}
            </>
          )}
        </div>
        
        {/* Action buttons row - always visible */}
        {lead.status !== 'Converted' ? (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(lead); }}
              className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-blue-200"
            >
              <Edit2 size={14} />
              <span>Edit</span>
            </button>
            
            {/* Lost leads: Reopen replaces Convert */}
            {isLost && (
              <button
                onClick={(e) => { e.stopPropagation(); onUpdateStatus(lead.id, 'New'); }}
                className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-gray-200"
              >
                <RotateCcw size={14} />
                <span>Reopen</span>
              </button>
            )}

            {/* Convert / Create Job (not shown for Lost leads) */}
            {!isLost && (
            <button
              onClick={(e) => { e.stopPropagation(); onConvert(lead); }}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center justify-center gap-1.5 border ${
                hasCompleteAddress(lead) 
                  ? isReturningCustomer
                    ? 'text-blue-600 hover:bg-blue-50 border-blue-200 font-medium'  // Returning - blue
                    : 'text-green-600 hover:bg-green-50 border-green-200'           // New - green
                  : 'text-gray-400 cursor-not-allowed border-gray-200'
              }`}
              disabled={!hasCompleteAddress(lead)}
              title={
                !hasCompleteAddress(lead) 
                  ? 'Complete address info first' 
                  : isReturningCustomer
                    ? 'Create a new job for this existing customer'
                    : 'Create customer and job'
              }
            >
              {isReturningCustomer ? (
                <>
                  <Briefcase size={14} />
                  <span>Create Job</span>
                </>
              ) : (
                <>
                  <UserCheck size={14} />
                  <span>Convert</span>
                </>
              )}
            </button>
            
            )}

            {/* OWNER ONLY: Delete button */}
            {isOwner && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
                className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-red-200"
              >
                <Trash2 size={14} />
                <span>Delete</span>
              </button>
            )}
          </div>
        ) : (
          /* Mobile: Show converted status since dropdown is hidden */
          <div className="flex items-center gap-2">
            <span className="sm:hidden text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-lg font-medium">
              ✓ Converted to Customer
            </span>
            {/* OWNER ONLY: remove a converted lead record (customer and job are kept) */}
            {isOwner && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
                className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-red-200"
              >
                <Trash2 size={14} />
                <span>Delete</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;